import {
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import { Button, Carousel, Empty, Modal, Pagination, Space, Tag, Tooltip, Typography, message } from 'antd';
import React, { useMemo, useState } from 'react';
import './index.less';
import { getGenerationBatchDetail } from '@/services/qnMock';

export type GenRecordStatus = 'queued' | 'running' | 'success' | 'failed';

export interface GenRecordItem {
  id: string;
  title: string;
  prompt: string;
  modelLabel: string;
  createdAtText: string;
  status: GenRecordStatus;
  kind?: 'video' | 'image';
  coverUrl?: string;
  videoUrl?: string;
  durationText?: string;
  resolutionText?: string;
  modelNameText?: string;
  jobIdText?: string;
  costSecondsText?: string;
  pointsText?: string;
  createdAtFullText?: string;
  referenceThumbUrl?: string;
}

export interface GenerationRecordsProps {
  title?: string;
  items?: GenRecordItem[];
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  emptyText?: string;
  onClear?: () => void;
  onDeleteItem?: (id: string) => Promise<void> | void;
}

type PreviewMediaKind = 'image' | 'video';

type PreviewMedia = {
  url: string;
  kind: PreviewMediaKind;
};

function statusMeta(status: GenRecordStatus): { text: string; tone: 'blue' | 'green' | 'red' | 'gold' } {
  if (status === 'success') return { text: '已完成', tone: 'green' };
  if (status === 'failed') return { text: '失败', tone: 'red' };
  if (status === 'running') return { text: '生成中', tone: 'blue' };
  return { text: '排队中', tone: 'gold' };
}

function inferMediaKind(url: string, fallback: PreviewMediaKind = 'image'): PreviewMediaKind {
  const clean = String(url || '').split('?')[0].toLowerCase();
  if (/\.(mp4|mov|webm|m3u8)$/.test(clean)) return 'video';
  if (/\.(png|jpe?g|webp|gif|bmp|avif)$/.test(clean)) return 'image';
  return fallback;
}

function appendQuery(url: string, query: string) {
  if (!url) return '';
  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
}

function isTencentCosUrl(url: string) {
  const u = String(url || '').toLowerCase();
  return u.includes('.cos.') && u.includes('myqcloud.com');
}

function buildCosVideoCoverUrl(videoUrl: string) {
  return appendQuery(videoUrl, 'ci-process=snapshot&time=1&format=jpg&width=160&height=120');
}

function pickCreateRoute(scope: string) {
  const s = String(scope || '').toLowerCase();
  if (s === 'grok') return '/grok-video';
  if (s === 'veo') return '/veo-video';
  if (s === 'sora2') return '/sora2-video';
  if (s === 'keling') return '/keling-video';
  if (s === 'sendance') return '/sendance-video';
  if (s === 'nano-banana-pro') return '/banana-pro';
  if (s === 'gpt-image') return '/gpt-image';
  return '/home';
}

function extFromUrl(url: string) {
  const clean = String(url || '').split('?')[0];
  const m = clean.match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : '';
}

function triggerDownload(url: string, filename?: string) {
  const href = String(url || '').trim();
  if (!href) return;
  try {
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    if (filename) a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
}

function parseRefUrlsField(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x || '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x || '').trim()).filter(Boolean);
      }
    } catch (e) {
      // ignore parse error
    }
    if (s.includes(',')) {
      return s.split(',').map((x) => x.trim()).filter(Boolean);
    }
    return [s];
  }
  return [];
}

export const GenerationRecords: React.FC<GenerationRecordsProps> = ({
  title = '生成记录',
  items = [],
  total,
  page,
  pageSize,
  onPageChange,
  emptyText = '暂无生成记录',
  onClear,
  onDeleteItem,
}) => {
  const safeItems = Array.isArray(items) ? items : [];
  const isEmpty = safeItems.length === 0;
  const headerMeta = useMemo(() => {
    if (isEmpty) return '最近 0 条';
    return `最近 ${Math.min(safeItems.length, 20)} 条`;
  }, [isEmpty, safeItems.length]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchItems, setBatchItems] = useState<any[]>([]);
  const [previewMode, setPreviewMode] = useState<'result' | 'reference'>('result');
  const [resultPreviewIndex, setResultPreviewIndex] = useState(0);
  const [referencePreviewIndex, setReferencePreviewIndex] = useState(0);

  const carouselRef = React.useRef<any>(null);
  const activeItem = useMemo(
    () => safeItems.find((it) => it.id === activeId),
    [activeId, safeItems],
  );

  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!activeId) return;
      setBatchLoading(true);
      setBatchItems([]);
      setPreviewMode('result');
      setResultPreviewIndex(0);
      setReferencePreviewIndex(0);
      try {
        const { data } = await getGenerationBatchDetail(activeId);
        if (cancelled) return;
        setBatchItems(Array.isArray(data) ? data : []);
      } finally {
        if (!cancelled) setBatchLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  return (
    <div className="genrec-card">
      <div className="genrec-head">
        <div>
          <div className="genrec-title">{title}</div>
          <div className="genrec-subtitle">{headerMeta}</div>
        </div>
        {onClear ? (
          <Button size="small" icon={<DeleteOutlined />} onClick={onClear}>
            清空
          </Button>
        ) : null}
      </div>

      <div className="genrec-body">
        {isEmpty ? (
          <div className="genrec-empty">
            <Empty description={emptyText} />
          </div>
        ) : (
          <div className="genrec-list">
            {safeItems.map((it) => {
              const meta = statusMeta(it.status);
              const icon = it.kind === 'image' ? <PictureOutlined /> : <VideoCameraOutlined />;
              const canDelete = it.status === 'failed';
              return (
                <div
                  key={it.id}
                  className="genrec-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveId(it.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setActiveId(it.id);
                  }}
                >
                  <div className="genrec-item-icon">{icon}</div>
                  <div className="genrec-item-main">
                    <div className="genrec-item-top">
                      <Typography.Text className="genrec-item-title" ellipsis>
                        {it.title}
                      </Typography.Text>
                      <Space size={8}>
                        <Tag className="genrec-tag" bordered={false}>
                          {it.modelLabel}
                        </Tag>
                        <Tag className={`genrec-status genrec-status-${meta.tone}`} bordered={false}>
                          {meta.text}
                        </Tag>
                        {onDeleteItem ? (
                          <Tooltip title={canDelete ? '删除' : '仅失败任务可删除'}>
                            <Button
                              size="small"
                              type="text"
                              icon={<DeleteOutlined />}
                              disabled={!canDelete}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!canDelete) {
                                  message.warning('排队中/生成中/成功任务不可删除，仅失败任务可删除');
                                  return;
                                }
                                try {
                                  await onDeleteItem?.(it.id);
                                  if (activeId === it.id) setActiveId(null);
                                } catch (err: any) {
                                  message.error(String(err?.message || '删除失败'));
                                }
                              }}
                            />
                          </Tooltip>
                        ) : null}
                      </Space>
                    </div>
                    <Typography.Paragraph className="genrec-item-prompt" ellipsis={{ rows: 2 }}>
                      {it.prompt}
                    </Typography.Paragraph>
                    <div className="genrec-item-meta">
                      <ClockCircleOutlined />
                      <span>{it.createdAtText}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {typeof total === 'number' && typeof page === 'number' && typeof pageSize === 'number' ? (
        <div className="genrec-pagination">
          <Pagination
            size="small"
            total={total}
            current={page}
            pageSize={pageSize}
            showSizeChanger
            pageSizeOptions={[5, 10, 20, 30]}
            showTotal={(t) => `共 ${t} 条`}
            onChange={(p, ps) => onPageChange?.(p, ps)}
          />
        </div>
      ) : null}
      <Modal
        open={!!activeItem}
        onCancel={() => setActiveId(null)}
        footer={null}
        width={1040}
        destroyOnHidden
        className="genrec-modal"
      >
        {activeItem ? (() => {
          const activeMeta = statusMeta(activeItem.status);
          const resultRows = (batchItems || []).filter((x) => x && (x.resultUrl || x.resultText));
          const resultMediaList: PreviewMedia[] = resultRows
            .map((m) => {
              const url = String(m?.resultUrl ?? '').trim();
              if (!url) return null;
              return {
                url,
                kind: inferMediaKind(url, activeItem.kind === 'video' ? 'video' : 'image'),
              } as PreviewMedia;
            })
            .filter((x): x is PreviewMedia => !!x);

          const refUrlSet = new Set<string>();
          for (const row of batchItems || []) {
            parseRefUrlsField((row as any)?.refUrls).forEach((u) => refUrlSet.add(u));
          }
          if (activeItem.referenceThumbUrl) {
            refUrlSet.add(activeItem.referenceThumbUrl);
          }
          const referenceMediaList: PreviewMedia[] = Array.from(refUrlSet).map((u) => ({
            url: u,
            kind: inferMediaKind(u, 'image'),
          }));

          const hasResult = resultMediaList.length > 0;
          const hasReference = referenceMediaList.length > 0;
          const currentMode: 'result' | 'reference' =
            hasResult
              ? (previewMode === 'reference' && hasReference ? 'reference' : 'result')
              : (hasReference ? 'reference' : 'result');
          const previewList = currentMode === 'reference' ? referenceMediaList : resultMediaList;
          const selectedIndex = currentMode === 'reference' ? referencePreviewIndex : resultPreviewIndex;
          const safeIndex = Math.min(selectedIndex, Math.max(0, previewList.length - 1));
          const currentResult = resultRows[Math.min(resultPreviewIndex, Math.max(0, resultRows.length - 1))]
            || resultRows[0];
          const showLeftPreview = batchLoading || previewList.length > 0;

          return (
          <div className={`genrec-modal-shell${showLeftPreview ? '' : ' no-left'}`}>
            {showLeftPreview ? (
              <div className="genrec-modal-left">
                <div className="genrec-modal-video">
                  {batchLoading ? (
                    <div className="genrec-modal-video-placeholder">加载中…</div>
                  ) : previewList.length > 0 ? (
                    <div className="genrec-carousel">
                    <Carousel
                      key={`preview-${currentMode}-${previewList.length}`}
                      ref={carouselRef}
                      dots={false}
                      initialSlide={safeIndex}
                      afterChange={(idx) => {
                        if (currentMode === 'reference') setReferencePreviewIndex(idx);
                        else setResultPreviewIndex(idx);
                      }}
                    >
                      {previewList.map((m, idx) => {
                        const url = m.url;
                        return (
                          <div key={`${url}_${idx}`} className="genrec-carousel-slide">
                            {m.kind === 'image' ? (
                              <img className="genrec-carousel-img" src={url} alt="" />
                            ) : (
                              <video className="genrec-carousel-video" src={url} controls playsInline />
                            )}
                          </div>
                        );
                      })}
                    </Carousel>
                    <div className="genrec-carousel-thumbs">
                      {previewList.map((m, idx) => {
                        const url = m.url;
                        const active = idx === safeIndex;
                    const showVideoCover = m.kind === 'video' && isTencentCosUrl(url);
                    const coverUrl = showVideoCover ? buildCosVideoCoverUrl(url) : '';
                        return (
                          <button
                            type="button"
                            key={`${url}_thumb_${idx}`}
                            className={`genrec-thumb${active ? ' active' : ''}`}
                            onClick={() => {
                              if (currentMode === 'reference') setReferencePreviewIndex(idx);
                              else setResultPreviewIndex(idx);
                              carouselRef.current?.goTo?.(idx);
                            }}
                          >
                            {m.kind === 'image' ? (
                              <img src={url} alt="" />
                            ) : (
                          showVideoCover ? (
                            <img src={coverUrl} alt="" />
                          ) : (
                            <div className="genrec-thumb-video genrec-thumb-video-processing">
                              <div className="genrec-thumb-video-icon">
                                <VideoCameraOutlined />
                              </div>
                              <div className="genrec-thumb-video-text">生成中</div>
                            </div>
                          )
                            )}
                          </button>
                        );
                      })}
                    </div>
                      <div className="genrec-preview-switch">
                        <button
                          type="button"
                          className={`genrec-preview-switch-btn${currentMode === 'result' ? ' active' : ''}`}
                          disabled={!hasResult}
                          onClick={() => setPreviewMode('result')}
                        >
                          预览结果
                        </button>
                        <button
                          type="button"
                          className={`genrec-preview-switch-btn${currentMode === 'reference' ? ' active' : ''}`}
                          disabled={!hasReference}
                          onClick={() => setPreviewMode('reference')}
                        >
                          预览参考素材
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="genrec-modal-right">
              <div className="genrec-modal-block">
                <div className="genrec-modal-block-head">
                  <div className="genrec-modal-block-title">创作描述</div>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      const p = String(currentResult?.prompt ?? activeItem.prompt ?? '');
                      if (!p) return;
                      navigator.clipboard.writeText(p).then(() => {
                        message.success('已复制描述');
                      });
                    }}
                  >
                    复制描述
                  </Button>
                </div>
                <div className="genrec-modal-desc">{String(currentResult?.prompt ?? activeItem.prompt ?? '')}</div>
              </div>

              <div className="genrec-modal-block">
                <div className="genrec-modal-block-title">创作信息</div>
                <div className="genrec-modal-info-row">
                  <span className="label">状态</span>
                  <span className={`value status status-${activeMeta.tone}`}>{activeMeta.text}</span>
                </div>
                {activeItem.status === 'failed' ? (
                  <div className="genrec-modal-info-row">
                    <span className="label">失败原因</span>
                    <span className="value">{String(currentResult?.errorMsg ?? '—')}</span>
                  </div>
                ) : null}
                <div className="genrec-modal-info-row">
                  <span className="label">使用模型</span>
                  <span className="value">
                    {activeItem.modelNameText || activeItem.modelLabel || '-'}
                  </span>
                </div>
                <div className="genrec-modal-info-row">
                  <span className="label">任务 ID</span>
                  <span className="value">
                    <Space size={6}>
                      <span>{activeItem.jobIdText || '-'}</span>
                      {activeItem.jobIdText ? (
                        <Tooltip title="复制">
                          <Button
                            size="small"
                            type="link"
                            style={{ padding: 0, height: 'auto' }}
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(activeItem.jobIdText || '');
                              message.success('已复制任务 ID');
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </Space>
                  </span>
                </div>
                <div className="genrec-modal-info-row">
                  <span className="label">处理耗时</span>
                  <span className="value">{activeItem.costSecondsText || '-'}</span>
                </div>
                <div className="genrec-modal-info-row">
                  <span className="label">创作时间</span>
                  <span className="value">
                    {activeItem.createdAtFullText || activeItem.createdAtText}
                  </span>
                </div>
                <div className="genrec-modal-info-row">
                  <span className="label">消耗积分</span>
                  <span className="value highlight">
                    {activeItem.pointsText || '—'}
                  </span>
                </div>
                <div className="genrec-modal-actions">
                  <Button
                    type="primary"
                    onClick={() => {
                      const selected =
                        currentMode === 'result'
                          ? previewList[safeIndex]
                          : (resultMediaList[0] as any | undefined);
                      const url = String(selected?.url || '').trim();
                      if (!url) {
                        message.warning('暂无可下载的结果');
                        return;
                      }
                      const ext = extFromUrl(url) || (selected?.kind === 'video' ? 'mp4' : 'png');
                      const base = String(activeItem.title || 'result').replace(/\s+/g, '_').slice(0, 32);
                      triggerDownload(url, `${base}.${ext}`);
                    }}
                  >
                    下载
                  </Button>
                </div>
              </div>

              <div className="genrec-modal-footer">
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={() => {
                    const scope = String(activeItem.modelLabel || '').trim();
                    const route = pickCreateRoute(scope);
                    const row: any = currentResult || {};
                    history.push(route, {
                      prefill: {
                        prompt: String(row?.prompt ?? activeItem.prompt ?? ''),
                        urls: parseRefUrlsField(row?.refUrls),
                        modelKey: String(row?.modelKey ?? ''),
                        ratioKey: String(row?.aspectRatio ?? ''),
                        sizeKey: String(row?.imageSize ?? ''),
                      },
                    });
                    setActiveId(null);
                    message.success('已带入参数，开始创作同款');
                  }}
                >
                  创作同款
                </Button>
              </div>
            </div>
          </div>
        ); })() : null}
      </Modal>
    </div>
  );
};
